/**
 * Deep merge utility for merging HMO-specific translations into base translations
 * HMO overrides take precedence over base values
 */

/**
 * Deeply merges two objects, with source values taking precedence over target values
 * @param target - Base object (base translations)
 * @param source - Override object (HMO-specific translations)
 * @returns Merged object with source values overriding target values
 */
export function deepMerge<T extends Record<string, any>>(
  target: T,
  source: Partial<T>
): T {
  const output = { ...target } as T;

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      const sourceValue = source[key];
      const targetValue = target[key];

      if (isObject(sourceValue) && isObject(targetValue)) {
        // Recursively merge nested objects
        (output as any)[key] = deepMerge(targetValue, sourceValue);
      } else if (isObject(sourceValue) && !isObject(targetValue)) {
        // Source is an object but target is not - use source as-is
        (output as any)[key] = sourceValue;
      } else if (sourceValue !== undefined) {
        // Source value takes precedence (including null)
        (output as any)[key] = sourceValue;
      }
    });
  }

  return output;
}

/**
 * Type guard to check if value is a plain object
 */
function isObject(item: any): item is Record<string, any> {
  return item && typeof item === 'object' && !Array.isArray(item);
}

